import React, { useCallback, useEffect, useState, useRef, useLayoutEffect, lazy, Component } from 'react';
import ReactDOM from 'react-dom';
import { CaseStudyHeader } from '../components/CaseStudyHeader';
import { Footer } from '../components/Footer';
import { CustomCursor } from '../components/CustomCursor';
import { CaseStudyNavigation } from '../components/CaseStudyNavigation';
import { CaseStudyMobileHeader } from '../components/CaseStudyMobileHeader';
import { FloatingButtons } from '../components/FloatingButtons';
import { ChatDrawer } from '../components/ChatDrawer';
import { usePortfolio } from '../context/PortfolioContext';
import { useCaseStudyNavigation } from '../hooks/useCaseStudyNavigation';
import { SEO } from '../components/SEO';
import { StructuredData } from '../components/StructuredData';
import { ArrowLeftIcon, CheckCircleIcon, X, ZoomIn, ChevronRight, Target, Clock, Users, Zap, BarChart3, TrendingUp } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/usbank.css';
import { useScrollAnimation } from '../utils/AnimationObserver';
import '../styles/animations.css';
import { motion, useScroll, useTransform } from 'framer-motion';
// Optimized Image Component
function SimpleImage({
  src,
  alt,
  className = ''
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  return <div className={`overflow-hidden rounded-lg relative ${className}`} style={{
    contain: 'content'
  }} data-id="element-1142">
      {!isLoaded && <div className="w-full h-full bg-gray-100 absolute inset-0 animate-pulse" data-id="element-1143" />}
      <img src={src} alt={alt} loading="lazy" decoding="async" onLoad={() => setIsLoaded(true)} className={`w-full h-auto transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`} data-id="element-1144" />
    </div>;
}
// Vertical Timeline Component (Replacing HorizontalTimeline)
function VerticalTimeline() {
  const timelineItems = [{
    year: 'Phase 1',
    title: 'Content & Research',
    desc: 'Dedicated space for content strategy and user research findings.'
  }, {
    year: 'Phase 2',
    title: 'Development Collaboration',
    desc: 'Active design work with real-time feedback loops.'
  }, {
    year: 'Phase 3',
    title: 'Ready for Dev',
    desc: 'Finalized designs with accessibility annotations and handoff specs.'
  }];
  return <div className="relative py-12 ml-4 sm:ml-8" data-id="element-1145">
      {/* Vertical Line */}
      <div className="absolute left-[11px] top-0 bottom-0 w-0.5 bg-gray-200" data-id="element-1146" />

      <div className="space-y-24" data-id="element-1147">
        {timelineItems.map((item, index) => <motion.div key={index} initial={{
        opacity: 0,
        y: 50
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6,
        delay: index * 0.2
      }} className="relative flex items-start gap-8" data-id="element-1148">
            {/* Timeline Dot */}
            <div className="relative z-10 flex-shrink-0 w-6 h-6 rounded-full bg-[#001E79] ring-8 ring-white shadow-sm mt-8" data-id="element-1149" />

            {/* Content Card */}
            <div className="flex-1 bg-white p-8 rounded-2xl shadow-xl border border-gray-100 flex flex-col gap-4 group hover:-translate-y-2 transition-transform duration-300" data-id="element-1150">
              <div className="w-12 h-1 bg-[#001E79] mb-2" data-id="element-1151" />
              <span className="text-xs font-bold text-slate-400 tracking-widest uppercase" data-id="element-1152">
                {item.year}
              </span>
              <h3 className="text-2xl font-medium text-[#001E79]" data-id="element-1153">
                {item.title}
              </h3>
              <p className="text-slate-600 leading-relaxed text-base" data-id="element-1154">
                {item.desc}
              </p>
            </div>
          </motion.div>)}
      </div>
    </div>;
}
// Modal Portal Component
function ModalPortal({
  children,
  isOpen
}: {
  children: React.ReactNode;
  isOpen: boolean;
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);
  if (!mounted || !isOpen) return null;
  return ReactDOM.createPortal(children, document.body);
}
// Lightbox Image Component
function LightboxImage({
  src,
  alt,
  label
}: {
  src: string;
  alt: string;
  label?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  return <>
      <div className="relative group cursor-pointer overflow-hidden rounded-lg bg-gray-50 border border-gray-100" onClick={() => setIsOpen(true)} data-id="element-1155">
        <img src={src} alt={alt} className="w-full h-auto transition-transform duration-500 group-hover:scale-[1.02]" data-id="element-1156" />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1157">
          <div className="opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300" data-id="element-1158">
            <div className="bg-white text-black px-4 py-2 rounded-full text-sm font-medium shadow-lg flex items-center gap-2" data-id="element-1159">
              <ZoomIn className="w-4 h-4" data-id="element-1160" />
              Zoom
            </div>
          </div>
        </div>
      </div>

      <ModalPortal isOpen={isOpen} data-id="element-1161">
        <div onClick={() => setIsOpen(false)} className="fixed inset-0 bg-white/95 z-[99999] flex items-center justify-center p-4 sm:p-8 backdrop-blur-sm" data-id="element-1162">
          <button onClick={e => {
          e.stopPropagation();
          setIsOpen(false);
        }} className="absolute top-6 right-6 p-3 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors" data-id="element-1163">
            <X className="w-6 h-6 text-black" data-id="element-1164" />
          </button>

          {label && <div className="absolute top-8 left-8 text-black text-xl font-medium" data-id="element-1165">
              {label}
            </div>}

          <img src={src} alt={alt} onClick={e => e.stopPropagation()} className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl" data-id="element-1166" />
        </div>
      </ModalPortal>
    </>;
}
interface CaseStudyUSBankProps {
  isOverlay?: boolean;
}
export function CaseStudyUSBank({
  isOverlay = false
}: CaseStudyUSBankProps) {
  const {
    viewPreference,
    linkedInProfile,
    isChatDrawerOpen,
    navigatedFromChat
  } = usePortfolio();
  const navigate = useNavigate();
  // UPDATED: Check if user came from chat OR home page
  const cameFromChat = sessionStorage.getItem('cameFromChat') === 'true';
  const cameFromHome = sessionStorage.getItem('navigatedFromHome') === 'true';
  const showBackButton = cameFromChat || cameFromHome;
  const {
    openChatDrawer,
    previousPage,
    setPreviousPage
  } = usePortfolio();
  // UPDATED: Handle back navigation to home
  const handleBackToHome = () => {
    // Clear navigation flags
    sessionStorage.removeItem('cameFromChat');
    sessionStorage.removeItem('navigatedFromHome');
    // Navigate first
    navigate('/');
    // FIXED: Scroll to top AFTER navigation completes
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };
  const homePath = viewPreference === 'ai' ? '/portfolio/ai' : '/portfolio/traditional';
  useCaseStudyNavigation();
  useScrollAnimation();
  // CRITICAL: Reset scroll position BEFORE any rendering
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);
  const [activeSection, setActiveSection] = useState('summary');
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['summary', 'objectives', 'discovery', 'process', 'results'];
      const scrollPosition = window.scrollY + 200;
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(section);
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 100,
        behavior: 'smooth'
      });
    }
  };
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleCaseStudyClick = (slug: string) => {
    console.log('Case study navigation handled by ChatDrawer:', slug);
  };
  const navItems = [{
    id: 'summary',
    label: 'Overview'
  }, {
    id: 'objectives',
    label: 'Objectives'
  }, {
    id: 'discovery',
    label: 'Problem'
  }, {
    id: 'process',
    label: 'Solution'
  }, {
    id: 'results',
    label: 'Impact'
  }];
  return <>
      <SEO title="US Bank Commercial Card - UX Case Study" description="Redesigning the corporate card application process." keywords={['UX Design', 'Fintech', 'Case Study']} url="https://mateocohen.com/case-study/us-bank" type="article" image="https://uploadthingy.s3.us-west-1.amazonaws.com/cGTn1zWyBRLJq9z8XJ288f/usbthumb.png" data-id="element-1167" />

      {!isOverlay && <CustomCursor data-id="element-1168" />}

      <div className="flex min-h-screen bg-white text-black font-switzer" data-id="element-1169">
        <div className="flex-1 min-w-0 flex flex-col relative" data-id="element-1170">
          {!isOverlay && <CaseStudyHeader data-id="element-1171" />}
          {!isOverlay && <CaseStudyMobileHeader data-id="element-1172" />}

          {/* Hero Section - Clean White Background */}
          <section className="pt-32 pb-12 px-4 sm:px-8 md:px-12 lg:px-24 max-w-[1400px] mx-auto w-full relative" style={{
          zIndex: 100
        }} data-id="element-1173">
            <div className="max-w-4xl" data-id="element-1174">
              {/* Back to Portfolio button - always visible */}
              {!isOverlay && <button onClick={handleBackToHome} className="flex items-center gap-2 text-gray-600 hover:text-[#001E79] transition-colors mb-6 group" aria-label="Back to portfolio" style={{
              marginBottom: '28px'
            }} data-id="element-1175">
                  <ArrowLeftIcon className="w-5 h-5 group-hover:-translate-x-1 transition-transform" data-id="element-1176" />
                  <span className="text-sm font-medium" data-id="element-1177">Back to Portfolio</span>
                </button>}

              <h1 className="text-5xl sm:text-6xl md:text-6xl font-light tracking-tight mb-8 text-[#001E79] leading-[1.1]" data-id="element-1178">
                U.S. Bank Commercial Card
              </h1>
              <p className="text-xl sm:text-2xl text-gray-600 leading-relaxed max-w-2xl font-light" data-id="element-1179">
                Transforming a 54-day manual corporate card application process
                into a streamlined digital platform.
              </p>
            </div>
          </section>

          {/* Full Width Hero Image */}
          <div className="w-full h-[60vh] sm:h-[80vh] overflow-hidden mb-24 relative" style={{
          zIndex: 100
        }} data-id="element-1180">
            <img src="https://cdn.magicpatterns.com/uploads/qmk3kLu5tjWSuKq1Da1MRB/usbcasestudyhomepage.png" alt="US Bank Hero" className="w-full h-full object-cover object-top" data-id="element-1181" />
          </div>

          {/* Main Content Grid */}
          <div className="max-w-[1400px] mx-auto w-full px-4 sm:px-8 md:px-12 lg:px-24 pb-32 relative" style={{
          zIndex: 100
        }} data-id="element-1182">
            <div className="lg:grid lg:grid-cols-12 gap-12" data-id="element-1183">
              {/* Sticky Navigation Sidebar */}
              <div className="hidden lg:block lg:col-span-3" data-id="element-1184">
                <div className="sticky top-32 space-y-6" data-id="element-1185">
                  {navItems.map(item => <button key={item.id} onClick={() => scrollToSection(item.id)} className={`block text-sm tracking-wide transition-colors duration-300 ${activeSection === item.id ? 'text-[#001E79] font-medium border-l-2 border-[#001E79] pl-4' : 'text-gray-400 hover:text-gray-600 pl-4 border-l-2 border-transparent'}`} data-id="element-1186">
                      {item.label}
                    </button>)}
                </div>
              </div>

              {/* Content Column */}
              <div className="lg:col-span-9 space-y-32" data-id="element-1187">
                {/* Summary Section */}
                <section id="summary" className="scroll-mt-32" data-id="element-1188">
                  <div className="grid md:grid-cols-2 gap-16 mb-24" data-id="element-1189">
                    <div data-id="element-1190">
                      <h3 className="text-sm font-medium text-[#001E79] uppercase tracking-wider mb-2" data-id="element-1191">
                        Role
                      </h3>
                      <p className="text-lg text-slate-700" data-id="element-1192">Lead UX Designer</p>
                    </div>
                    <div data-id="element-1193">
                      <h3 className="text-sm font-medium text-[#001E79] uppercase tracking-wider mb-2" data-id="element-1194">
                        Timeline
                      </h3>
                      <p className="text-lg text-slate-700" data-id="element-1195">2022-2023</p>
                    </div>
                  </div>

                  <h2 className="text-3xl font-light mb-8 text-[#001E79]" data-id="element-1196">
                    Overview
                  </h2>
                  <p className="text-lg text-slate-600 leading-relaxed mb-12 max-w-3xl" data-id="element-1197">
                    In 2022, I joined U.S. Bank's Team Flash to refine a newly
                    launched digital onboarding experience for corporate card
                    applications. The challenge was to transform a{' '}
                    <span className="font-semibold text-slate-900" data-id="element-1198">
                      54-day manual process into a streamlined digital platform
                    </span>{' '}
                    while addressing critical usability issues that emerged
                    after launch.
                  </p>

                  <div className="bg-gray-50 rounded-2xl p-8 md:p-12 mb-12" data-id="element-1199">
                    <h3 className="text-xl font-medium mb-8 text-[#001E79]" data-id="element-1200">
                      Key Achievements
                    </h3>
                    <div className="space-y-4" data-id="element-1201">
                      {['Achieved <span class="font-semibold text-slate-900">98% customer rating of "easy" or "very easy"</span> for digital onboarding, exceeding 90% target', 'Reduced bank handling time by <span class="font-semibold text-slate-900">65%: from 54 days to just 20 days</span>', 'Successfully migrated <span class="font-semibold text-slate-900">96% of CPS pipeline to digital onboarding</span>, far surpassing 80% target', 'Unlocked <span class="font-semibold text-slate-900">20,000 hours of annual productivity</span> through streamlined digital solutions', 'Implemented <span class="font-semibold text-slate-900">17+ automated onboarding processes</span> using ServiceNow'].map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-1202">
                          <CheckCircleIcon className="w-5 h-5 text-[#001E79] mt-1 flex-shrink-0" data-id="element-1203" />
                          <span className="text-slate-700 text-lg" dangerouslySetInnerHTML={{
                        __html: item
                      }} data-id="element-1204" />
                        </div>)}
                    </div>
                  </div>
                </section>

                {/* Objectives Section */}
                <section id="objectives" className="scroll-mt-32" data-id="element-1205">
                  <h2 className="text-3xl font-light mb-8 text-[#001E79]" data-id="element-1206">
                    Objectives
                  </h2>
                  <p className="text-lg text-slate-600 leading-relaxed mb-12 max-w-3xl" data-id="element-1207">
                    This project aimed to transform corporate card onboarding
                    while achieving specific business outcomes and user
                    experience goals.
                  </p>

                  <div className="space-y-4 mb-24" data-id="element-1208">
                    {[{
                    title: 'Best-in-Class Experience',
                    desc: 'Deliver a seamless digital onboarding experience that rivals consumer fintech standards.'
                  }, {
                    title: 'Revenue Opportunities',
                    desc: 'Accelerate time-to-revenue by drastically reducing application processing times.'
                  }, {
                    title: 'Expand Impact',
                    desc: 'Create scalable workflows that can be deployed across multiple bank product lines.'
                  }, {
                    title: '>90% Satisfaction',
                    desc: 'Achieve customer satisfaction ratings exceeding 90% for ease of use.'
                  }, {
                    title: '>50% Time Reduction',
                    desc: 'Cut manual processing time by more than half through automation and self-service.'
                  }].map((card, idx) => <div key={idx} className="border border-gray-200 p-6 rounded-lg hover:border-gray-300 transition-colors" data-id="element-1209">
                        <div className="flex items-start gap-4" data-id="element-1210">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full border-2 border-[#001E79] flex items-center justify-center text-xs font-bold text-[#001E79]" data-id="element-1211">
                            {idx + 1}
                          </div>
                          <div className="flex-1" data-id="element-1212">
                            <h3 className="text-lg font-medium text-[#001E79] mb-2" data-id="element-1213">
                              {card.title}
                            </h3>
                            <p className="text-slate-600 leading-relaxed" data-id="element-1214">
                              {card.desc}
                            </p>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </section>

                {/* Discovery Section */}
                <section id="discovery" className="scroll-mt-32" data-id="element-1215">
                  <h2 className="text-3xl font-light mb-12 text-[#001E79]" data-id="element-1216">
                    Problem & Discovery
                  </h2>

                  <p className="text-lg text-slate-600 leading-relaxed mb-12 max-w-3xl" data-id="element-1217">
                    I inherited designs from a third-party agency that only
                    addressed the "happy path," but the live application
                    revealed significant problems across conditional user flows.
                  </p>

                  {/* Modern Problem Statement */}
                  <div className="mb-16 border-l-4 border-[#001E79] pl-8 py-2" data-id="element-1218">
                    <h3 className="text-sm font-bold text-[#001E79] uppercase tracking-wider mb-4" data-id="element-1219">
                      Problem Statement
                    </h3>
                    <p className="text-2xl font-light leading-tight text-slate-900" data-id="element-1220">
                      Corporate card applicants and internal processors
                      struggled with a{' '}
                      <span className="font-semibold text-slate-900" data-id="element-1221">
                        fragmented 54-day application process
                      </span>{' '}
                      requiring extensive manual coordination across multiple
                      stakeholders, leading to{' '}
                      <span className="font-semibold text-slate-900" data-id="element-1222">
                        confusion about task ownership, duplicated effort, and
                        inability to track application progress.
                      </span>
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8 mb-16" data-id="element-1223">
                    {[{
                    title: 'Disorganized Workflows',
                    desc: 'Missing Figma designs and development-design misalignment created confusion and delays.'
                  }, {
                    title: 'Inconsistent Design',
                    desc: 'Outdated components and poor navigation patterns hindered user experience.'
                  }, {
                    title: 'Accessibility Gaps',
                    desc: 'Poor contrast and non-compliant interactions limited usability and inclusivity.'
                  }, {
                    title: 'User Pain Points',
                    desc: 'Unclear navigation, limited user control, and dated visual identity frustrated users.'
                  }].map((card, idx) => <div key={idx} className="bg-gray-50 p-8 rounded-xl" data-id="element-1224">
                        <h4 className="font-medium text-lg mb-2 text-[#001E79]" data-id="element-1225">
                          {card.title}
                        </h4>
                        <p className="text-slate-600" data-id="element-1226">{card.desc}</p>
                      </div>)}
                  </div>
                </section>

                {/* Process Section */}
                <section id="process" className="scroll-mt-32" data-id="element-1227">
                  <h2 className="text-3xl font-light mb-12 text-[#001E79]" data-id="element-1228">
                    Solution & Methodology
                  </h2>

                  <p className="text-lg text-slate-600 leading-relaxed mb-6 max-w-3xl" data-id="element-1229">
                    As the sole designer on the team, I quickly realized I
                    needed to establish a sustainable workflow. The existing
                    process had designers, developers, and content writers
                    stepping on each other's toes in shared Figma files, causing
                    confusion and slowing everyone down. To solve this, I
                    developed a three-phase improvement strategy that would let
                    everyone work in their own space of expertise while
                    maintaining consistency across the entire application:
                  </p>

                  {/* Methodology Framework Visualization */}
                  <div className="mb-24" data-id="element-1230">
                    <h3 className="text-xl font-medium mb-8 text-[#001E79]" data-id="element-1231">
                      Strategic Approach
                    </h3>
                    <VerticalTimeline data-id="element-1232" />
                  </div>

                  <div className="space-y-32" data-id="element-1233">
                    {/* Step 1 - Image to the right */}
                    <div data-id="element-1234">
                      <div className="flex items-center gap-4 mb-6" data-id="element-1235">
                        <span className="text-4xl font-light text-slate-300" data-id="element-1236">
                          01
                        </span>
                        <h3 className="text-2xl font-medium text-[#001E79]" data-id="element-1237">
                          Chain Workflow System
                        </h3>
                      </div>

                      <div className="grid lg:grid-cols-2 gap-8 items-start" data-id="element-1238">
                        <div data-id="element-1239">
                          <p className="text-lg text-slate-600 leading-relaxed mb-6" data-id="element-1240">
                            With multiple stakeholders (product managers,
                            content specialists, and developers) all working in
                            the same Figma files, we needed a{' '}
                            <span className="font-semibold text-slate-900" data-id="element-1241">
                              single source of truth
                            </span>{' '}
                            that wouldn't create bottlenecks. I designed a{' '}
                            <span className="font-semibold text-slate-900" data-id="element-1242">
                              fluid content management system
                            </span>{' '}
                            where each piece of content lived as a component
                            that could be updated independently and
                            automatically sync across all design files.
                          </p>
                          <p className="text-lg text-slate-600 leading-relaxed mb-6" data-id="element-1243">
                            This{' '}
                            <span className="font-semibold text-slate-900" data-id="element-1244">
                              separation of content and design
                            </span>{' '}
                            was transformative. Our content strategist and I
                            could work simultaneously without interfering with
                            each other's workflow. Content updates in the
                            template instantly propagated to the design system
                            and then into all design files. As screens moved
                            into active sprints in Jira, I marked them "Ready
                            for Dev," giving the team clear visibility into what
                            was approved and ready to build.
                          </p>
                          <p className="text-lg text-slate-600 leading-relaxed" data-id="element-1245">
                            The result?{' '}
                            <span className="font-semibold text-slate-900" data-id="element-1246">
                              Our product leader noted we moved 30% more
                              efficiently
                            </span>{' '}
                            through screen updates. The image shows a remake of
                            these content documents, each laid out for easy
                            scanning, each a swappable component that maintained
                            consistency across the entire application.
                          </p>
                        </div>

                        <div className="bg-gray-50 p-6 rounded-2xl" data-id="element-1247">
                          <SimpleImage src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/bdovfglt8tusyvarlsnd9f/5eeb8de3-4a4e-4cf2-9740-92477376f38e/Content_Template.png" alt="Workflow System" data-id="element-1248" />
                        </div>
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div data-id="element-1249">
                      <div className="flex items-center gap-4 mb-6" data-id="element-1250">
                        <span className="text-4xl font-light text-slate-300" data-id="element-1251">
                          02
                        </span>
                        <h3 className="text-2xl font-medium text-[#001E79]" data-id="element-1252">
                          Accessibility as Definition of Done
                        </h3>
                      </div>

                      <div className="grid lg:grid-cols-2 gap-8 items-start" data-id="element-1253">
                        <div data-id="element-1254">
                          <p className="text-lg text-slate-600 leading-relaxed mb-6" data-id="element-1255">
                            I partnered with accessibility specialist Tracey
                            Jackson to ensure every screen met{' '}
                            <span className="font-semibold text-slate-900" data-id="element-1256">
                              WCAG 2.1 standards
                            </span>
                            . The accessibility annotations you see here became
                            part of our "Ready for Dev" phase, our team's{' '}
                            <span className="font-semibold text-slate-900" data-id="element-1257">
                              definition of done
                            </span>{' '}
                            before any design went to engineering.
                          </p>
                          <p className="text-lg text-slate-600 leading-relaxed" data-id="element-1258">
                            Content and I would review these annotations
                            together, syncing with the organization's design
                            system while discussing improvements. This
                            collaboration directly{' '}
                            <span className="font-semibold text-slate-900" data-id="element-1259">
                              improved usability for users with visual
                              disabilities
                            </span>{' '}
                            and gave engineers clear, actionable specifications.
                          </p>
                        </div>

                        <div className="bg-gray-50 p-6 rounded-2xl" data-id="element-1260">
                          <SimpleImage src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/bdovfglt8tusyvarlsnd9f/5eeb8de3-4a4e-4cf2-9740-92477376f38e/Annotations_master_example.png" alt="Accessibility Annotations" data-id="element-1261" />
                        </div>
                      </div>
                    </div>

                    {/* Step 3 */}
                    <div data-id="element-1262">
                      <div className="flex items-center gap-4 mb-6" data-id="element-1263">
                        <span className="text-4xl font-light text-slate-300" data-id="element-1264">
                          03
                        </span>
                        <h3 className="text-2xl font-medium text-[#001E79]" data-id="element-1265">
                          Design System Alignment
                        </h3>
                      </div>
                      <p className="text-lg text-slate-600 leading-relaxed mb-6 max-w-3xl" data-id="element-1266">
                        I collaborated with the design system team to establish
                        a consistent, modern aesthetic. I{' '}
                        <span className="font-semibold text-slate-900" data-id="element-1267">
                          updated 75% of outdated components
                        </span>{' '}
                        and created nearly 20 new local components, including a
                        vertical navigation bar that was later adopted by other
                        teams.
                      </p>
                      <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-3xl" data-id="element-1268">
                        In the screens presented here, the progress bar was
                        removed from the "Get Started" screen by design. After
                        analyzing user feedback, I discovered the 5-step count
                        created a perception of length that didn't match the
                        actual effort required. By consolidating the intro
                        screen into the "Get Started" flow, we{' '}
                        <span className="font-semibold text-slate-900" data-id="element-1269">
                          reduced the visible step count to 4
                        </span>
                        . Users noted the process felt significantly shorter,
                        even though we'd only reduced the perceived complexity,
                        not the actual screens.
                      </p>
                      <div className="grid md:grid-cols-2 gap-8" data-id="element-1270">
                        <div data-id="element-1271">
                          <p className="text-sm font-medium text-slate-500 mb-4 uppercase tracking-wide" data-id="element-1272">
                            Before
                          </p>
                          <LightboxImage src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/bdovfglt8tusyvarlsnd9f/5eeb8de3-4a4e-4cf2-9740-92477376f38e/Old_design.png" alt="Old Design" data-id="element-1273" />
                        </div>
                        <div data-id="element-1274">
                          <p className="text-sm font-medium text-slate-500 mb-4 uppercase tracking-wide" data-id="element-1275">
                            After
                          </p>
                          <LightboxImage src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/bdovfglt8tusyvarlsnd9f/5eeb8de3-4a4e-4cf2-9740-92477376f38e/New_Design.png" alt="New Design" data-id="element-1276" />
                        </div>
                      </div>
                    </div>

                    {/* Step 4 */}
                    <div data-id="element-1277">
                      <div className="flex items-center gap-4 mb-6" data-id="element-1278">
                        <span className="text-4xl font-light text-slate-300" data-id="element-1279">
                          04
                        </span>
                        <h3 className="text-2xl font-medium text-[#001E79]" data-id="element-1280">
                          Reimagined User Journey
                        </h3>
                      </div>
                      <p className="text-lg text-slate-600 leading-relaxed mb-6 max-w-3xl" data-id="element-1281">
                        With our new workflow running smoothly, I finally had
                        the bandwidth to tackle the user experience improvements
                        that had been on the backlog. The streamlined process
                        gave me room to{' '}
                        <span className="font-semibold text-slate-900" data-id="element-1282">
                          map out complex user flows
                        </span>{' '}
                        that would have been impossible to manage before.
                      </p>
                      <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-3xl" data-id="element-1283">
                        The most impactful change was introducing a
                        <span className="font-semibold text-slate-900" data-id="element-1284">
                          {' '}
                          collaborative workflow system
                        </span>{' '}
                        for the application itself. Though it looks simple, a
                        collaborators section in the lower left, this feature
                        required extensive user flow mapping. It gave
                        administrators clear visibility into who was responsible
                        for what, and let multiple stakeholders complete their
                        assigned portions independently. Account owners could
                        now{' '}
                        <span className="font-semibold text-slate-900" data-id="element-1285">
                          reassign roles on the fly
                        </span>
                        , and users could skip questions and return later,
                        addressing two of our biggest pain points.
                      </p>
                      <div className="bg-gray-50 p-8 rounded-2xl" data-id="element-1286">
                        <SimpleImage src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/bdovfglt8tusyvarlsnd9f/5eeb8de3-4a4e-4cf2-9740-92477376f38e/Collaborators.png" alt="Collaborators Section" data-id="element-1287" />
                      </div>
                    </div>
                  </div>
                </section>

                {/* Results Section */}
                <section id="results" className="scroll-mt-32" data-id="element-1288">
                  <h2 className="text-3xl font-light mb-12 text-[#001E79]" data-id="element-1289">
                    Business Impact & Outcomes
                  </h2>

                  <p className="text-lg text-slate-600 leading-relaxed mb-12 max-w-3xl" data-id="element-1290">
                    The new application sections launched to positive feedback
                    from both users and stakeholders. What started as a chaotic
                    design process became a model of collaboration and
                    efficiency that other teams at the bank started to
                    reference.
                  </p>

                  {/* Data Visualizations */}
                  <div className="space-y-16 mb-24" data-id="element-1291">
                    {/* Time Reduction & Satisfaction Grid */}
                    <div className="grid md:grid-cols-2 gap-8" data-id="element-1292">
                      {/* Time Reduction */}
                      <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm" data-id="element-1293">
                        <h3 className="text-xl font-medium mb-6 text-[#001E79]" data-id="element-1294">
                          Processing Time Reduction
                        </h3>
                        <div className="space-y-8" data-id="element-1295">
                          {/* Before */}
                          <div data-id="element-1296">
                            <div className="flex justify-between items-baseline mb-3" data-id="element-1297">
                              <span className="text-sm font-medium text-slate-500 uppercase tracking-wide" data-id="element-1298">
                                Before
                              </span>
                              <span className="text-2xl font-bold text-slate-700" data-id="element-1299">
                                54 Days
                              </span>
                            </div>
                            <div className="h-3 bg-gray-100 rounded-full overflow-hidden" data-id="element-1300">
                              <div className="h-full bg-slate-400 w-full rounded-full" data-id="element-1301"></div>
                            </div>
                          </div>

                          {/* After */}
                          <div data-id="element-1302">
                            <div className="flex justify-between items-baseline mb-3" data-id="element-1303">
                              <span className="text-sm font-medium text-[#001E79] uppercase tracking-wide" data-id="element-1304">
                                After
                              </span>
                              <span className="text-2xl font-bold text-[#001E79]" data-id="element-1305">
                                20 Days
                              </span>
                            </div>
                            <div className="h-3 bg-gray-100 rounded-full overflow-hidden" data-id="element-1306">
                              <div className="h-full bg-[#001E79] rounded-full" style={{
                              width: '37%'
                            }} data-id="element-1307"></div>
                            </div>
                          </div>

                          {/* Impact Badge */}
                          <div className="pt-4 border-t border-gray-100" data-id="element-1308">
                            <div className="inline-flex items-center gap-2 bg-gray-100 text-slate-700 px-4 py-2.5 rounded-lg" data-id="element-1309">
                              <span className="text-sm font-bold" data-id="element-1310">
                                65% Faster Processing
                              </span>
                            </div>
                            <p className="text-xs text-slate-500 mt-3" data-id="element-1311">
                              Apply: ~10 days | Onboarding: &lt;25 days
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Satisfaction */}
                      <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm" data-id="element-1312">
                        <h3 className="text-xl font-medium mb-6 text-[#001E79]" data-id="element-1313">
                          Customer Satisfaction
                        </h3>
                        <div className="flex flex-col items-center justify-center h-[calc(100%-3rem)]" data-id="element-1314">
                          {/* Circular Progress */}
                          <div className="relative w-44 h-44 mb-6" data-id="element-1315">
                            <svg className="w-full h-full transform -rotate-90" data-id="element-1316">
                              {/* Background Circle */}
                              <circle cx="88" cy="88" r="80" fill="none" stroke="#e2e8f0" strokeWidth="16" data-id="element-1317" />
                              {/* Progress Circle - 98% with visible gap */}
                              <circle cx="88" cy="88" r="80" fill="none" stroke="#001E79" strokeWidth="16" strokeDasharray={`${2 * Math.PI * 80}`} strokeDashoffset={`${2 * Math.PI * 80 * 0.05}`} strokeLinecap="round" className="transition-all duration-1000 ease-out" data-id="element-1318" />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-1319">
                              <span className="text-5xl font-light text-[#001E79]" data-id="element-1320">
                                98%
                              </span>
                              <span className="text-xs text-slate-500 uppercase tracking-wider mt-1" data-id="element-1321">
                                Satisfaction
                              </span>
                            </div>
                          </div>

                          {/* Description */}
                          <div className="text-center" data-id="element-1322">
                            <p className="text-sm text-slate-600 mb-3" data-id="element-1323">
                              Rated "Easy" or "Very Easy"
                            </p>
                            <div className="inline-flex items-center gap-2 bg-gray-100 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-medium" data-id="element-1324">
                              <span data-id="element-1325">Target: 90%</span>
                              <span className="text-slate-400" data-id="element-1326">•</span>
                              <span className="font-bold" data-id="element-1327">Achieved: 98%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Automation Impact Stats */}
                    <div className="bg-white border border-gray-200 rounded-2xl p-8 md:p-12 shadow-sm" data-id="element-1328">
                      <h3 className="text-2xl font-medium mb-8 text-[#001E79]" data-id="element-1329">
                        Automation & Productivity Impact
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-1330">
                        {[{
                        label: 'Automated Processes',
                        value: '17+',
                        icon: Zap
                      }, {
                        label: 'Annual Hours Saved',
                        value: '20k',
                        icon: Clock
                      }, {
                        label: 'Pipeline Migrated',
                        value: '96%',
                        icon: TrendingUp
                      }, {
                        label: 'Time Reduction',
                        value: '65%',
                        icon: BarChart3
                      }].map((stat, idx) => <div key={idx} className="text-center" data-id="element-1331">
                            <div className="w-14 h-14 bg-gray-50 border border-gray-200 rounded-xl flex items-center justify-center mx-auto mb-4" data-id="element-1332">
                              <stat.icon className="w-7 h-7 text-slate-600" data-id="element-1333" />
                            </div>
                            <div className="text-3xl font-light text-[#001E79] mb-2" data-id="element-1334">
                              {stat.value}
                            </div>
                            <div className="text-xs text-slate-500 uppercase tracking-wide leading-tight" data-id="element-1335">
                              {stat.label}
                            </div>
                          </div>)}
                      </div>
                    </div>
                  </div>

                  <p className="text-lg text-slate-600 leading-relaxed mb-16 max-w-3xl" data-id="element-1336">
                    Before my departure, our product owner highlighted these
                    achievements in a stakeholder presentation, and on my last
                    day, I had a chance to chat with my team members who
                    expressed their appreciation for my efforts. I truly enjoyed
                    working with everyone and made many friends along the way.
                  </p>

                  <div className="bg-[#001E79] text-white p-12 rounded-2xl relative" data-id="element-1337">
                    <h3 className="text-2xl font-light mb-8" data-id="element-1338">
                      What Users Said
                    </h3>
                    <div className="grid md:grid-cols-2 gap-12" data-id="element-1339">
                      <div data-id="element-1340">
                        <p className="text-lg leading-relaxed text-white mb-4" data-id="element-1341">
                          "Navigation was clear and accessible, making it easy
                          to find what I needed"
                        </p>
                        <p className="text-sm font-medium text-white" data-id="element-1342">
                          Clear Navigation
                        </p>
                      </div>
                      <div data-id="element-1343">
                        <p className="text-lg leading-relaxed text-white mb-4" data-id="element-1344">
                          "The collaborator view and edit was now easy to access
                          for my whole team"
                        </p>
                        <p className="text-sm font-medium text-white" data-id="element-1345">
                          Collaborator Access
                        </p>
                      </div>
                      <div data-id="element-1346">
                        <p className="text-lg leading-relaxed text-white mb-4" data-id="element-1347">
                          "Content is easy to read with no jargon, finally feels
                          professional"
                        </p>
                        <p className="text-sm font-medium text-white" data-id="element-1348">
                          Clear Content
                        </p>
                      </div>
                      <div data-id="element-1349">
                        <p className="text-lg leading-relaxed text-white mb-4" data-id="element-1350">
                          "'Skip for Now' was greatly appreciated by those
                          working through the application at their own pace"
                        </p>
                        <p className="text-sm font-medium text-white" data-id="element-1351">
                          Flexible Completion
                        </p>
                      </div>
                    </div>

                    {/* Right-aligned Back to Top Button - Only for this case study */}
                  </div>
                </section>
              </div>
            </div>
          </div>

          {!isOverlay && <CaseStudyNavigation homePath={homePath} previousCase={{
          category: 'Featured Work',
          title: 'CWRU',
          slug: 'case-western'
        }} nextCase={{
          category: 'Case Studies',
          title: 'Cyber Security',
          slug: 'cyber-security'
        }} data-id="element-1352" />}

          {!isOverlay && <Footer data-id="element-1353" />}
        </div>

        {!isOverlay && <ChatDrawer onCaseStudyClick={handleCaseStudyClick} data-id="element-1354" />}
      </div>

      {!isOverlay && !isChatDrawerOpen && <FloatingButtons data-id="element-1355" />}
    </>;
}